// =============================================================================
// Routes — Partner control surface (partners.oelox.com)
// =============================================================================

export const ROUTES = {
  LOGIN: "/login",
  CHANGE_PASSWORD: "/change-password",
  APP_ROOT: "/app",
  DASHBOARD: "/app/dashboard",
  ORDERS: "/app/orders",
  DRIVERS: "/app/drivers",
  CLIENTS: "/app/clients",
  ANALYTICS: "/app/analytics",
  WALLET: "/app/wallet",
  SETTINGS: "/app/settings",
} as const;

/** Client report page — `/app/clients/:id`. */
export function clientDetailPath(tenantId: string): string {
  return `${ROUTES.CLIENTS}/${encodeURIComponent(tenantId)}`;
}

/** True when the pathname is a client report page. */
export function isClientDetailPath(pathname: string): boolean {
  return /^\/app\/clients\/[^/]+$/.test(pathname);
}

/** Extract the client id from a client report pathname (null when not one). */
export function clientIdFromPath(pathname: string): string | null {
  if (!isClientDetailPath(pathname)) return null;
  const raw = pathname.slice(`${ROUTES.CLIENTS}/`.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Driver page — `/app/drivers/:id`. */
export function driverDetailPath(driverId: string): string {
  return `${ROUTES.DRIVERS}/${encodeURIComponent(driverId)}`;
}

/** True when the pathname is a driver page. */
export function isDriverDetailPath(pathname: string): boolean {
  return /^\/app\/drivers\/[^/]+$/.test(pathname);
}

/** Extract the driver id from a driver pathname (null when not one). */
export function driverIdFromPath(pathname: string): string | null {
  if (!isDriverDetailPath(pathname)) return null;
  const raw = pathname.slice(`${ROUTES.DRIVERS}/`.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

export const APP_ROUTE_PATHS: ReadonlySet<string> = new Set([
  ROUTES.APP_ROOT,
  ROUTES.DASHBOARD,
  ROUTES.ORDERS,
  ROUTES.DRIVERS,
  ROUTES.CLIENTS,
  ROUTES.ANALYTICS,
  ROUTES.WALLET,
  ROUTES.SETTINGS,
]);

/** Exact app route or a detail page (client report / driver page). */
export function isAppPath(pathname: string): boolean {
  return (
    APP_ROUTE_PATHS.has(pathname) ||
    isClientDetailPath(pathname) ||
    isDriverDetailPath(pathname)
  );
}
